import { formatBytes, matchesFileType } from '../../shared-ui-layer/types/validation.types'

import { getConfig } from '../../core-layer/config'
import {
  extractBucketFromUrl,
  getSourceFromBucket,
  extractFilePath,
  getCDNMapping,
  extractCDNFilePath,
  isGif,
  isRelativePath,
} from './url.utils'
import type { ValidationError } from '../../shared-ui-layer/types/validation.types'
import type { DRMImageOptions, ImageValidation } from '../types'

export type { ImageFormat, ResizeMode, GravityPosition, DRMImageOptions, ImageValidation } from '../types'

/**
 * Generates a DRM-protected image URL with optimization parameters
 *
 * @param options - Image options including licenseId or src
 * @returns Optimized DRM image URL or original src if GIF
 *
 * @example
 * // With licenseId
 * const url = generateDRMImageUrl({
 *   licenseId: 'abc123',
 *   width: 800,
 *   quality: 90,
 *   format: 'webp'
 * })
 *
 * @example
 * // With src URL (auto-converts to DRM)
 * const url = generateDRMImageUrl({
 *   src: 'https://storage.googleapis.com/bucket/image.jpg',
 *   width: 600,
 *   quality: 85
 * })
 *
 * @example
 * // With relative path (membership bucket path without hostname)
 * const url = generateDRMImageUrl({
 *   src: '/memberships/knES3eSWYIsc5YSZ3YLl/product/4f5f8dd4-33f4-406c-bfc9-9eaa063cf23c',
 *   width: 800
 * })
 * // Returns: 'https://assetsdrm.clientclub.net/images/courses/gcs_revex-membership-production/memberships/knES3eSWYIsc5YSZ3YLl/product/4f5f8dd4-33f4-406c-bfc9-9eaa063cf23c?fmt=webp&qlt=90&wdt=800&rsz=fill'
 */
export function generateDRMImageUrl(options: DRMImageOptions): string {
  const {
    licenseId,
    src,
    source = 'communities', // Only used for licenseId mode; auto-detected from bucket for GCS URLs
    width,
    height,
    format = 'webp',
    quality = 90,
    resize = 'fill',
    gravity,
    dpr,
    blur,
    sharpen,
    pixelate,
    rotate,
    bgColor,
    padding,
    maxBytes,
  } = options

  const config = getConfig()

  // Build base URL based on what's available
  let baseUrl = ''

  if (licenseId) {
    // Format: {IMAGE_STREAM_BASE_URL}/images/{source}/lid_{licenseId}
    baseUrl = `${config.ASSETS_DRM_IMAGE_URL}/images/${source}/lid_${licenseId}`
  } else if (src) {
    // If it's a GIF, return original URL (GIFs not optimized via DRM)
    if (isGif(src)) {
      return src
    }

    // Handle relative paths (e.g., /memberships/abc/product/xyz or memberships/abc/product/xyz)
    // These are file paths from the membership bucket stored without a full URL.
    // We treat them as belonging to the current environment's membership bucket.
    if (isRelativePath(src)) {
      const filePath = src.startsWith('/') ? src.substring(1) : src
      const bucket = config.FIREBASE.membershipStorageBucket
      const detectedSource = getSourceFromBucket(bucket)
      const finalSource = detectedSource || source

      // Use current environment's DRM URL (relative paths are always from the current env)
      baseUrl = `${config.ASSETS_DRM_IMAGE_URL}/images/${finalSource}/gcs_${bucket}/${filePath}`
    } else {
      // Full URL - check if it's from a known CDN
      const cdnMapping = getCDNMapping(src)

      if (cdnMapping) {
        // CDN URL detected - use mapped bucket
        const bucket = cdnMapping.bucket

        // Extract file path from CDN URL (entire pathname without leading slash)
        const filePath = extractCDNFilePath(src)

        // Try to auto-detect source from bucket name
        const detectedSource = getSourceFromBucket(bucket)

        // Use detected source if available, otherwise fallback to passed source
        const finalSource = detectedSource || source

        // Determine DRM base URL based on CDN environment
        let drmBaseUrl: string

        if (cdnMapping.environment === 'production') {
          // Production CDN → production DRM (remove 'staging.' subdomain)
          drmBaseUrl = config.ASSETS_DRM_IMAGE_URL.replace('staging.', '')
        } else {
          // Staging CDN → ensure staging DRM (add 'staging.' if missing)
          drmBaseUrl = config.ASSETS_DRM_IMAGE_URL.includes('staging.')
            ? config.ASSETS_DRM_IMAGE_URL
            : config.ASSETS_DRM_IMAGE_URL.replace('https://', 'https://staging.')
        }

        // Convert CDN URL to DRM URL
        // Format: {DRM_BASE_URL}/images/{finalSource}/gcs_{bucket}/{filePath}
        baseUrl = `${drmBaseUrl}/images/${finalSource}/gcs_${bucket}/${filePath}`
      } else {
        // Not a CDN URL - try to extract bucket from GCS URL
        const bucket = extractBucketFromUrl(src)

        // If we can't extract a bucket, it's not a GCS URL we can optimize
        if (!bucket) {
          console.warn('⚠️ Cannot extract bucket from URL, returning original:', src)
          return src
        }

        // Try to auto-detect source from bucket name
        const detectedSource = getSourceFromBucket(bucket)

        // Use detected source if available, otherwise fallback to passed source
        const finalSource = detectedSource || source

        // Extract file path
        const filePath = extractFilePath(src)

        // Detect environment from bucket name (production vs staging)
        // Match bucket environment with appropriate DRM endpoint
        const bucketLower = bucket.toLowerCase()
        const isProductionBucket = bucketLower.includes('production')
        const isStagingBucket = bucketLower.includes('staging')

        // Determine DRM base URL based on bucket environment
        let drmBaseUrl: string

        if (isProductionBucket) {
          // Production bucket → production DRM (remove 'staging.' subdomain)
          drmBaseUrl = config.ASSETS_DRM_IMAGE_URL.replace('staging.', '')
        } else if (isStagingBucket) {
          // Staging bucket → ensure staging DRM (add 'staging.' if missing)
          drmBaseUrl = config.ASSETS_DRM_IMAGE_URL.includes('staging.')
            ? config.ASSETS_DRM_IMAGE_URL
            : config.ASSETS_DRM_IMAGE_URL.replace('https://', 'https://staging.')
        } else {
          // Unknown bucket → use current environment config
          drmBaseUrl = config.ASSETS_DRM_IMAGE_URL
        }

        // Convert regular URL to DRM URL using auto-detected or fallback source
        // Format: {DRM_BASE_URL}/images/{finalSource}/gcs_{bucket}/{filePath}
        baseUrl = `${drmBaseUrl}/images/${finalSource}/gcs_${bucket}/${filePath}`
      }
    }
  } else {
    // No valid source provided
    return ''
  }

  // Build query parameters (same for both licenseId and sourceInfo paths)
  const params = new URLSearchParams()

  // Add all specified parameters
  params.append('fmt', format)
  params.append('qlt', quality.toString())
  if (width) params.append('wdt', width.toString())
  if (height) params.append('hgt', height.toString())
  if (resize) params.append('rsz', resize)
  if (gravity) params.append('grv', gravity)
  if (dpr) params.append('dpr', dpr.toString())
  if (blur) params.append('blu', blur.toString())
  if (sharpen) params.append('shp', sharpen.toString())
  if (pixelate) params.append('pix', pixelate.toString())
  if (rotate) params.append('rot', rotate.toString())
  if (bgColor) params.append('bg', bgColor)
  if (padding) params.append('pd', padding)
  if (maxBytes) params.append('mb', maxBytes.toString())

  return `${baseUrl}?${params.toString()}`
}

/**
 * Generates a thumbnail URL (smaller, lower quality for previews).
 * Uses width: 100, quality: 70, format: webp.
 *
 * @param options - Image options (licenseId or src required)
 * @returns Optimized thumbnail URL
 *
 * @example
 * generateThumbnailUrl({ licenseId: 'abc123', source: 'courses' })
 * // Returns: '{DRM_URL}/images/courses/lid_abc123?fmt=webp&qlt=70&wdt=100&rsz=fill'
 *
 * @example
 * generateThumbnailUrl({ src: 'https://storage.googleapis.com/bucket/image.jpg' })
 * // Returns: '{DRM_URL}/images/courses/gcs_bucket/image.jpg?fmt=webp&qlt=70&wdt=100&rsz=fill'
 */
export function generateThumbnailUrl(options: Pick<DRMImageOptions, 'licenseId' | 'src' | 'source'>): string {
  return generateDRMImageUrl({
    ...options,
    width: 100,
    quality: 70,
    format: 'webp',
  })
}

/**
 * Generates a blurred placeholder URL (for loading states).
 * Uses width: 50, quality: 30, blur: 90, format: webp — produces a tiny blurry image
 * suitable for progressive loading placeholders.
 *
 * @param options - Image options (licenseId or src required)
 * @returns Blurred placeholder URL
 *
 * @example
 * generateBlurPlaceholderUrl({ licenseId: 'abc123', source: 'courses' })
 * // Returns: '{DRM_URL}/images/courses/lid_abc123?fmt=webp&qlt=30&wdt=50&rsz=fill&blu=90'
 *
 * @example
 * generateBlurPlaceholderUrl({ src: 'https://storage.googleapis.com/bucket/image.jpg' })
 * // Returns: '{DRM_URL}/images/courses/gcs_bucket/image.jpg?fmt=webp&qlt=30&wdt=50&rsz=fill&blu=90'
 */
export function generateBlurPlaceholderUrl(options: Pick<DRMImageOptions, 'licenseId' | 'src' | 'source'>): string {
  return generateDRMImageUrl({
    ...options,
    width: 50,
    quality: 30,
    blur: 90,
    format: 'webp',
  })
}

/**
 * Get image dimensions from a file by creating a temporary Image element.
 * Client-side only (uses URL.createObjectURL).
 *
 * @param file - Image File object
 * @returns Promise resolving to { width, height } in pixels
 *
 * @example
 * const file = inputElement.files[0]
 * const { width, height } = await getImageDimensions(file)
 * // { width: 1920, height: 1080 }
 */
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.width, height: img.height })
      URL.revokeObjectURL(img.src)
    }
    img.onerror = reject
    img.src = URL.createObjectURL(file)
  })
}

/**
 * Validate image files against size, type, and dimension rules.
 *
 * NOTE: This function only validates file size, type, and dimensions.
 * maxFiles validation is handled at the composable level (usePostEditor, CommentEditor, etc.)
 * to allow selecting multiple files and then enforcing the limit when adding to the model.
 *
 * @param files - Array of File objects to validate
 * @param validation - Validation rules (ImageValidation)
 * @returns Object with valid files and validation errors
 *
 * @example
 * const { valid, errors } = await validateImageFiles(
 *   fileList,
 *   { maxFileSize: 5 * 1024 * 1024, acceptedTypes: ['image/jpeg', 'image/png'], maxDimensions: { width: 4096, height: 4096 } }
 * )
 * // valid: File[] — files that passed all checks
 * // errors: ValidationError[] — details for each failed file
 */
export const validateImageFiles = async (
  files: File[],
  validation: ImageValidation
): Promise<{ valid: File[]; errors: ValidationError[] }> => {
  const errors: ValidationError[] = []
  const validFiles: File[] = []

  for (const file of files) {
    const fileErrors: ValidationError[] = []

    // File size validation
    if (validation.maxFileSize && file.size > validation.maxFileSize) {
      fileErrors.push({
        field: 'fileSize',
        message: `Image "${file.name}" exceeds ${formatBytes(validation.maxFileSize)}`,
        type: 'error',
        meta: { fileName: file.name, size: file.size, maxSize: validation.maxFileSize },
      })
    }

    if (validation.minFileSize && file.size < validation.minFileSize) {
      fileErrors.push({
        field: 'fileSize',
        message: `Image "${file.name}" is smaller than ${formatBytes(validation.minFileSize)}`,
        type: 'error',
        meta: { fileName: file.name, size: file.size, minSize: validation.minFileSize },
      })
    }

    // MIME type validation
    if (validation.acceptedTypes && validation.acceptedTypes.length > 0) {
      const isAccepted = validation.acceptedTypes.some(acceptedType => matchesFileType(file.type, acceptedType))
      if (!isAccepted) {
        fileErrors.push({
          field: 'fileType',
          message: `File type "${file.type}" not accepted for "${file.name}"`,
          type: 'error',
          meta: { fileName: file.name, fileType: file.type, acceptedTypes: validation.acceptedTypes },
        })
      }
    }

    // Dimension validation
    if (validation.maxDimensions || validation.minDimensions) {
      try {
        const dimensions = await getImageDimensions(file)

        if (validation.maxDimensions) {
          if (dimensions.width > validation.maxDimensions.width || dimensions.height > validation.maxDimensions.height) {
            fileErrors.push({
              field: 'dimensions',
              message: `Image "${file.name}" exceeds maximum dimensions (${validation.maxDimensions.width}x${validation.maxDimensions.height})`,
              type: 'error',
              meta: {
                fileName: file.name,
                dimensions,
                maxDimensions: validation.maxDimensions,
              },
            })
          }
        }

        if (validation.minDimensions) {
          if (dimensions.width < validation.minDimensions.width || dimensions.height < validation.minDimensions.height) {
            fileErrors.push({
              field: 'dimensions',
              message: `Image "${file.name}" is smaller than minimum dimensions (${validation.minDimensions.width}x${validation.minDimensions.height})`,
              type: 'error',
              meta: {
                fileName: file.name,
                dimensions,
                minDimensions: validation.minDimensions,
              },
            })
          }
        }
      } catch (error) {
        console.error('Failed to get image dimensions:', error)
      }
    }

    if (fileErrors.length === 0) {
      validFiles.push(file)
    } else {
      errors.push(...fileErrors)
    }
  }

  return { valid: validFiles, errors }
}
